import { ReactNode, useEffect, useRef, useState } from 'react';
import { KeyNavRow } from '@/shared/keynav';

export type DropdownItem = {
  label: ReactNode;
  icon?: ReactNode;
  // Not needed on an `info` row.
  onClick?: () => void;
  tone?: 'default' | 'danger';
  divider?: boolean;
  // The page the user is on (a section collapsed into "More"): aria-current
  // plus a teal dot, so the open menu says where you are.
  active?: boolean;
  // A plain text row ("Signed in as …"): not a button, never focused.
  info?: boolean;
};

type Props = {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  // When true, the trigger button is removed from keyboard tab order.
  // Useful for header-chrome shortcuts that shouldn't interrupt the Tab
  // sequence through the main nav (e.g. the "+" Create button next to the
  // logo — users can still click it, but Tab skips over it).
  skipTabOrder?: boolean;
  // Accessible name of the trigger button (and of its menu) when the trigger
  // content does not say what the menu is: an icon, a gear, "More".
  label?: string;
  // Classes for the menu's outer wrapper. "hidden md:inline-block" hides the
  // whole menu below md, trigger button included, so it is not left behind
  // as an invisible Tab stop when only its content is hidden. (Tailwind emits
  // `hidden` after `inline-block`, so it wins over the wrapper's own display.)
  className?: string;
  // Extra classes for the menu panel itself (a height cap with scrolling, a
  // width cap).
  panelClassName?: string;
};

export function DropdownMenu({ trigger, items, align = 'right', skipTabOrder, label, className, panelClassName }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close and hand focus back to the trigger (Escape, an item chosen), so a
  // keyboard user is not dropped to <body>. A skip-tab-order trigger is never
  // given focus: it has no keyboard way out.
  function closeToTrigger() {
    setOpen(false);
    if (!skipTabOrder) triggerRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return;
    // An outside click only closes: the click already put focus where the
    // user wanted it.
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase + preventDefault: the menu closes first and marks the key
    // used, so a Modal around it stays open and SubNav does not pop the record.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeToTrigger();
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);

    // Move focus to the first item on open so arrow keys work immediately.
    // Run on next tick because the popover's DOM has just mounted.
    const t = setTimeout(() => {
      const first = ref.current?.querySelector<HTMLElement>(
        '[role="menu"] button:not([disabled]), [role="menu"] [role="menuitem"]:not([aria-disabled="true"])'
      );
      first?.focus();
    }, 0);

    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative inline-block${className ? ` ${className}` : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        // Keyboard focus outlines the trigger CONTENT, 1px outside it: the
        // content is opaque (company/branch pickers, Print pill, Add widget,
        // an active More) and would cover an inset ring on the button, and
        // the outline follows the content's own corners whatever its radius.
        className="focus:outline-none [&:focus-visible>*]:outline [&:focus-visible>*]:outline-2 [&:focus-visible>*]:outline-primary [&:focus-visible>*]:outline-offset-1"
        onClick={() => setOpen((v) => !v)}
        tabIndex={skipTabOrder ? -1 : undefined}
        // When the trigger is marked skip-tab-order (e.g. the "+" Create
        // button next to the logo), also stop the native focus transfer
        // that happens on mousedown. Otherwise clicking the button would
        // park focus on it, trapping keyboard users with no arrow handler
        // to move away. onClick still fires on mouseup.
        onMouseDown={skipTabOrder ? (e) => e.preventDefault() : undefined}
      >
        {trigger}
      </button>
      {open && (
        <KeyNavRow
          layout="vertical"
          role="menu"
          ariaLabel={label || 'Menu'}
          className={`animate-dropdown absolute top-full mt-1.5 ${align === 'right' ? 'right-0' : 'left-0'} z-40 min-w-[220px] rounded-xl border border-border bg-surface py-1.5 shadow-lg${panelClassName ? ` ${panelClassName}` : ''}`}
        >
          <div
            style={{ boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04)' }}
          >
            {items.map((item, i) => (
              <div key={i}>
                {item.divider && <div className="my-1 border-t border-border-light" />}
                {item.info ? (
                  <div className="flex items-center gap-2.5 px-3 py-1.5 text-[0.75rem] text-secondary">
                    {item.icon && <span className="w-4 h-4 flex items-center justify-center">{item.icon}</span>}
                    <span className="flex-1">{item.label}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    aria-current={item.active ? 'page' : undefined}
                    onClick={() => { closeToTrigger(); item.onClick?.(); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-[0.8125rem] text-left hover:bg-slate-50 transition ${
                      item.tone === 'danger' ? 'text-danger' : 'text-primary-text'
                    } ${item.active ? 'font-semibold' : ''}`}
                  >
                    {item.icon && <span className="w-4 h-4 flex items-center justify-center text-secondary">{item.icon}</span>}
                    <span className="flex-1">{item.label}</span>
                    {item.active && <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                  </button>
                )}
              </div>
            ))}
          </div>
        </KeyNavRow>
      )}
    </div>
  );
}
